// components/auth/PendingApprovalPage.js - Fixed version without infinite loops
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import tunaLogo from '../../assets/tunasignalai_logo.svg';
import {
  Clock,
  Mail,
  RefreshCw,
  LogOut,
  CheckCircle,
  User,
  AlertCircle,
  Bell,
  Shield,
  BarChart3,
  Loader2,
  Crown,
  Star,
  Zap,
  Building
} from 'lucide-react';
import { SiteStyles } from '../site/SiteChrome';

const PendingApprovalPage = () => {
  useTheme();
  const darkMode = true; // public and account pages always use the site's dark terminal theme
  const navigate = useNavigate();
  const { currentUser, logout, fetchUserProfile } = useAuth();
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(new Date());
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    // Check if user is already approved and redirect
    if (currentUser?.approval_status === 'approved' || currentUser?.is_approved) {
      navigate('/stockwatch', { replace: true });
    }
  }, [currentUser, navigate]);

  // FIXED: Enhanced status check without page reload
  const handleCheckStatus = async () => {
    setChecking(true);
    setStatusMessage('Checking account status...');
    
    try {
      console.log('Checking account status via API...');
      
      // Fetch fresh user data from the API
      const updatedUser = await fetchUserProfile(true);
      
      console.log('Updated user data:', {
        approval_status: updatedUser.approval_status,
        is_approved: updatedUser.is_approved,
        hasSubscription: !!updatedUser.subscription
      });
      
      // Check if user is now approved
      if (updatedUser.approval_status === 'approved' || updatedUser.is_approved) {
        console.log('User is now approved! Redirecting to dashboard...');
        setStatusMessage('Account approved! Redirecting to dashboard...');
        
        setTimeout(() => {
          navigate('/stockwatch', { replace: true });
        }, 1500);
        return;
      }
      
      // Still pending
      console.log('User is still pending approval');
      setStatusMessage('Account is still pending approval. Please check back later.');
      setLastChecked(new Date());
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setStatusMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error checking status:', error);
      setStatusMessage('Error checking status. Please try again.');
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setStatusMessage('');
      }, 3000);
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  const getTimeSinceRegistration = () => {
    if (!currentUser?.created_at) return 'Recently';
    
    const registered = new Date(currentUser.created_at);
    const now = new Date();
    const hours = Math.floor((now - registered) / (1000 * 60 * 60));
    
    if (hours < 1) return 'Less than an hour ago';
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const subscriptionInfo = React.useMemo(() => {
    // Check current user subscription first
    if (currentUser?.subscription) {
      return currentUser.subscription;
    }
    
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem('subscriptionInfo');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [currentUser]);

  return (
    <div className="dark ts-page">
    <SiteStyles />
    <div className={`min-h-screen ${
      darkMode ? 'bg-[#07131F]' : 'bg-slate-50 dark:bg-[#13283C]'
    } flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src={tunaLogo} 
            alt="TunaSignal AI" 
            className="h-12 mx-auto mb-4"
          />
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Account Pending Approval
          </h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Your account is being reviewed by our team
          </p>
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div className={`mb-6 p-4 rounded-lg border ${
            statusMessage.includes('approved') 
              ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
              : statusMessage.includes('Error') 
                ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
                : 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-[#13283C] dark:border-[#28445F] dark:text-blue-300'
          }`}>
            <div className="flex items-center">
              {statusMessage.includes('approved') ? (
                <CheckCircle className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
              ) : statusMessage.includes('Error') ? (
                <AlertCircle className="w-5 h-5 mr-2 text-red-600 dark:text-red-400" />
              ) : (
                <Clock className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              )}
              <span className="text-sm font-medium">{statusMessage}</span>
            </div>
          </div>
        )}

        {/* Main Content Card */}
        <div className={`${darkMode ? 'bg-[#0D1E2E] border border-[#1C3348]' : 'bg-white'} rounded-md overflow-hidden`}>
          {/* Success Header */}
          <div className="bg-[#0D1E2E] border-b border-[#1C3348] p-6">
            <div className="flex items-center justify-center text-white">
              <CheckCircle className="w-8 h-8 mr-3" />
              <div>
                <h2 className="text-2xl font-bold">Registration Complete!</h2>
                <p className="text-blue-100 mt-1">Welcome to TunaSignal AI, {currentUser?.first_name || 'User'}!</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Status Information */}
            <div className={`bg-slate-50 dark:bg-[#13283C] border ${
              darkMode ? 'border-[#28445F]' : 'border-blue-200'
            } rounded-lg p-6 mb-6`}>
              <div className="flex items-center mb-4">
                <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Account Review in Progress
                </h3>
              </div>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                Thank you for registering! Our team is reviewing your account.
                {subscriptionInfo 
                  ? ' Since you have an active subscription, this process is typically faster.' 
                  : ' This process typically takes 1-2 business days.'
                }
              </p>
              
              {/* Progress Steps */}
              <div className="space-y-3 mt-4">
                <div className="flex items-center space-x-3 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Registration submitted successfully</span>
                </div>
                <div className="flex items-center space-x-3 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Email verification completed</span>
                </div>
                {subscriptionInfo && (
                  <div className="flex items-center space-x-3 text-green-600 dark:text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">{subscriptionInfo.plan_name || 'Monthly'} subscription activated</span>
                  </div>
                )}
                <div className="flex items-center space-x-3 text-blue-600 dark:text-blue-400">
                  <div className="w-5 h-5 rounded-full border-2 border-blue-600 dark:border-blue-400 flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium">Admin review pending</span>
                </div>
              </div>

              {/* Subscription Badge if available */}
              {subscriptionInfo && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center">
                    <Crown className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                    <div>
                      <span className="text-sm font-semibold text-green-800 dark:text-green-300">
                        Your {subscriptionInfo.plan_name || 'Monthly Subscription'} is Ready!
                      </span>
                      <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                        Your subscription will be available immediately after account approval.
                        {subscriptionInfo.expires_at && (
                          <span className="block">
                            Expires: {new Date(subscriptionInfo.expires_at).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* User Details Card */}
            <div className={`${darkMode ? 'bg-[#13283C]' : 'bg-gray-50'} rounded-lg p-6 mb-6`}>
              <h4 className={`text-sm font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <User className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
                Account Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Username:</span>
                  <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {currentUser?.username}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Email:</span>
                  <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {currentUser?.email}
                  </span>
                </div>
                {currentUser?.company && (
                  <div className="flex items-center space-x-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Company:</span>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {currentUser.company}
                    </span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Registered:</span>
                  <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {getTimeSinceRegistration()}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <button
                onClick={handleCheckStatus}
                disabled={checking}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                  checking
                    ? 'bg-gray-300 dark:bg-[#1C3348] text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-[#5AA9F0] text-[#07131F] font-semibold hover:bg-[#8CC4F5]'
                }`}
              >
                {checking ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Checking Status...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2" />
                    Check Account Status
                  </>
                )}
              </button>

              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setLastChecked(new Date());
                    window.location.reload();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                    darkMode
                      ? 'bg-[#13283C] text-gray-300 hover:bg-[#1C3348]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Reload Page
                </button>
                
                <button
                  onClick={handleLogout}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                    darkMode
                      ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }`}
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Sign Out
                </button>
              </div>
            </div>

            {/* While You Wait Section */}
            <div className={`mt-6 p-4 ${
              darkMode ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'
            } border rounded-lg`}>
              <div className="flex items-start">
                <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 mr-3 ${
                  darkMode ? 'text-amber-400' : 'text-amber-600'
                }`} />
                <div className="flex-1">
                  <p className={`font-semibold mb-2 ${darkMode ? 'text-amber-300' : 'text-amber-900'}`}>
                    While You Wait:
                  </p>
                  <ul className={`space-y-1 text-sm ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Check your email (including spam folder) for updates</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>We'll notify you at <span className="font-medium">{currentUser?.email}</span></span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Don't create multiple accounts - this may delay approval</span>
                    </li>
                    {subscriptionInfo && (
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Your subscription is active and ready to use after approval</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Features Preview */}
            <div className={`mt-6 p-4 bg-slate-50 dark:bg-[#13283C] rounded-lg`}>
              <h4 className={`text-sm font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <Star className="w-4 h-4 text-yellow-500 mr-2" />
                What's Coming After Approval:
              </h4>
              <div className="grid grid-cols-1 gap-2 text-xs">
                {[
                  'AI-powered stock signals with advanced ranking',
                  'Complete alert system: MACD, TunaSignalAI Score, SEC filings, news, and volume spikes',
                  'Unlimited stock monitoring and watchlists',
                  'Fundamental and technical analysis summaries',
                  'Smart screening and filtering for high-potential setups',
                  'Mobile and web access with responsive design',
                  'Multi-channel alerts: Telegram, Email & SMS (coming soon)',
                  'Priority customer support and all future updates'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <Zap className="w-3 h-3 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Questions? Contact us at{' '}
            <a href="mailto:support@tunasignal.ai" className="text-blue-600 hover:text-blue-700 font-medium">
              support@tunasignal.ai
            </a>
          </p>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Last status check: {formatDate(lastChecked)}
          </p>
        </div>
      </div>
    </div>
    </div>
  );
};

export default PendingApprovalPage;